import {
  Button,
  Modal,
  Select,
  SelectOptionType,
  addToast,
} from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';

import {
  BulkInactivateContinuityCurriculum,
  BulkInactivateContinuityPeriod,
  requestBulkInactivateContinuityCurriculums,
  requestBulkInactivateContinuityExecute,
  requestBulkInactivateContinuityPeriods,
  requestBulkInactivateContinuityPreview,
} from '../../../api/requests/studentStatus';
import {
  SchoolsListItem,
  getAllSchoolsRequest,
} from '../../../api/requests/schools';
import ChipMultiSelect from '../../../components/form/ChipMultiSelect';
import { useValidations } from '../../../hooks/useValidations';
import BulkInactivateConfirmationModal from './BulkInactivateConfirmationModal';

interface BulkInactivateContinuityModalProps {
  isOpen: boolean;
  toggle: () => void;
  onAfterInactivate?: () => void;
}

interface FormValues {
  period: SelectOptionType | null;
  schoolIds: SelectOptionType[];
  curriculumIds: SelectOptionType[];
}

const DEFAULT_VALUES: FormValues = {
  period: null,
  schoolIds: [],
  curriculumIds: [],
};

const BulkInactivateContinuityModal = ({
  isOpen,
  toggle,
  onAfterInactivate,
}: BulkInactivateContinuityModalProps) => {
  const prefix = 'studentStatus.bulkInactivate';
  const { t } = useTranslation();
  const { msgValidations } = useValidations();

  const { control, handleSubmit, reset, setValue } = useForm<FormValues>({
    defaultValues: DEFAULT_VALUES,
  });

  const schoolIdsValue = useWatch({ control, name: 'schoolIds' });

  const [periods, setPeriods] = useState<BulkInactivateContinuityPeriod[]>([]);
  const [schools, setSchools] = useState<SchoolsListItem[]>([]);
  const [curriculums, setCurriculums] = useState<
    BulkInactivateContinuityCurriculum[]
  >([]);
  const [loaded, setLoaded] = useState(false);
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Load periods + schools once when the modal opens.
  useEffect(() => {
    if (!isOpen || loaded) {
      return;
    }
    let cancelled = false;
    (async () => {
      const [periodsRes, schoolsRes] = await Promise.all([
        requestBulkInactivateContinuityPeriods(),
        getAllSchoolsRequest(),
      ]);
      if (cancelled) {
        return;
      }
      if (!periodsRes.error && periodsRes.data) {
        setPeriods(periodsRes.data);
      }
      if (!schoolsRes.error && schoolsRes.data) {
        setSchools(schoolsRes.data);
      }
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, loaded]);

  // Cascade: when school selection changes, reload curriculums and reset
  // the curriculum selection so we never carry stale picks from another school.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    let cancelled = false;
    const schoolIds = (schoolIdsValue || []).map((o) => o.value as number);
    (async () => {
      const res = await requestBulkInactivateContinuityCurriculums({
        schoolIds: schoolIds.length > 0 ? schoolIds : undefined,
      });
      if (cancelled) {
        return;
      }
      if (!res.error && res.data) {
        setCurriculums(res.data);
      }
    })();
    setValue('curriculumIds', []);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, JSON.stringify((schoolIdsValue || []).map((o) => o.value))]);

  // Reset everything when the modal closes.
  useEffect(() => {
    if (isOpen) {
      return;
    }
    reset(DEFAULT_VALUES);
    setPreviewCount(null);
    setConfirmOpen(false);
    setLoading(false);
    setLoaded(false);
    setPeriods([]);
    setSchools([]);
    setCurriculums([]);
  }, [isOpen, reset]);

  const periodOptions = useMemo<SelectOptionType[]>(
    () =>
      periods.map((p) => ({
        value: p.id,
        label: p.name ?? String(p.id),
      })),
    [periods],
  );

  const schoolOptions = useMemo<SelectOptionType[]>(
    () =>
      schools.map((s) => ({
        value: s.id,
        label: s.name,
      })),
    [schools],
  );

  const curriculumOptions = useMemo<SelectOptionType[]>(
    () =>
      curriculums.map((c) => ({
        value: c.id,
        label: c.name ?? String(c.id),
      })),
    [curriculums],
  );

  const onSubmit = handleSubmit(async (data) => {
    if (!data.period) {
      return;
    }
    setLoading(true);
    try {
      const res = await requestBulkInactivateContinuityPreview({
        periodId: data.period.value as number,
        schoolIds: data.schoolIds.map((o) => o.value as number),
        curriculumIds: data.curriculumIds.map((o) => o.value as number),
      });
      if (res.error || !res.data) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${prefix}.errorToast`),
        });
        return;
      }
      setPreviewCount(res.data.count ?? 0);
      setConfirmOpen(true);
    } finally {
      setLoading(false);
    }
  });

  const onConfirmExecute = useCallback(
    async (formValues: FormValues) => {
      if (!formValues.period) {
        return;
      }
      setLoading(true);
      try {
        const res = await requestBulkInactivateContinuityExecute({
          periodId: formValues.period.value as number,
          schoolIds: formValues.schoolIds.map((o) => o.value as number),
          curriculumIds: formValues.curriculumIds.map((o) => o.value as number),
        });
        if (res.error || !res.data) {
          addToast({
            icon: 'error',
            color: 'danger',
            text: t(`${prefix}.errorToast`),
          });
          return;
        }
        const affected = res.data.affected ?? 0;
        const isDiscrepancy =
          previewCount !== null && affected !== previewCount;
        addToast({
          icon: 'success',
          color: 'success',
          text: t(
            isDiscrepancy
              ? `${prefix}.successToastDiscrepancy`
              : `${prefix}.successToast`,
            { count: affected, affected, previewCount },
          ),
        });
        setConfirmOpen(false);
        toggle();
        onAfterInactivate?.();
      } finally {
        setLoading(false);
      }
    },
    [previewCount, t, toggle, onAfterInactivate],
  );

  // We need the current form values when the user confirms in the second
  // modal — handleSubmit gives us that path without manually wiring watches.
  const triggerExecute = handleSubmit((data) => {
    return onConfirmExecute(data);
  });

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={loading ? () => undefined : toggle}
        size="md"
      >
        <Form onSubmit={onSubmit}>
          <Row className="pb-3">
            <Col xs={12} className="text-center">
              <h1 className="text-dark fs-22">{t(`${prefix}.modal.title`)}</h1>
            </Col>
          </Row>

          <Row>
            <Col xs={12}>
              <p className="fs-16 fw-300 text-center pb-3">
                {t(`${prefix}.modal.description`)}
              </p>
            </Col>
          </Row>

          <Row>
            <Col xs={12}>
              <Select
                name="period"
                label={t(`${prefix}.modal.fields.period`)}
                placeholder={t(`${prefix}.modal.fields.periodPlaceholder`)}
                options={periodOptions}
                control={control}
                rules={{ required: msgValidations.required }}
              />
            </Col>
          </Row>

          <Row>
            <Col xs={12}>
              <ChipMultiSelect
                name="schoolIds"
                label={t(`${prefix}.modal.fields.school`)}
                placeholder={t(`${prefix}.modal.fields.schoolPlaceholder`)}
                options={schoolOptions}
                control={control}
              />
            </Col>
          </Row>

          <Row>
            <Col xs={12}>
              <ChipMultiSelect
                name="curriculumIds"
                label={t(`${prefix}.modal.fields.curriculum`)}
                placeholder={t(`${prefix}.modal.fields.curriculumPlaceholder`)}
                options={curriculumOptions}
                control={control}
              />
            </Col>
          </Row>

          <Row className="pb-2 pt-4">
            <Col xs={12} sm={6}>
              <Button
                onClick={toggle}
                text={t(`${prefix}.actions.cancel`)}
                outlined
                fullwidth
                disabled={loading}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Button
                type="submit"
                text={t(`${prefix}.actions.confirm`)}
                fullwidth
                loading={loading}
                disabled={loading}
              />
            </Col>
          </Row>
        </Form>
      </Modal>

      {confirmOpen && previewCount !== null && (
        <BulkInactivateConfirmationModal
          isOpen={confirmOpen}
          toggle={() => {
            if (!loading) {
              setConfirmOpen(false);
            }
          }}
          count={previewCount}
          loading={loading}
          onConfirm={() => {
            void triggerExecute();
          }}
        />
      )}
    </>
  );
};

export default BulkInactivateContinuityModal;
